import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { Suspense } from "react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { NewsletterCTA } from "@/components/site/NewsletterCTA";
import { CiteThis } from "@/components/site/CiteThis";
import {
  listVolatilitySnapshots,
  listAlgorithmUpdates,
  type VolatilitySnapshot,
  type AlgorithmUpdate,
} from "@/lib/backlink.functions";
import { SITE, formatDate } from "@/lib/site";

const snapshotsQuery = queryOptions({
  queryKey: ["volatility-snapshots"],
  queryFn: () => listVolatilitySnapshots(),
});
const updatesQuery = queryOptions({
  queryKey: ["algorithm-updates"],
  queryFn: () => listAlgorithmUpdates(),
});

export const Route = createFileRoute("/local-search-index")({
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(snapshotsQuery),
      context.queryClient.ensureQueryData(updatesQuery),
    ]);
  },
  head: () => {
    const url = `${SITE.url}/local-search-index`;
    return {
      meta: [
        { title: `The Local Search Index — Map Pack Volatility | ${SITE.name}` },
        {
          name: "description",
          content:
            "Live weekly Map Pack volatility scores, recent algorithm updates, and a tracker of how the local search landscape is shifting — by Map Observer.",
        },
        { property: "og:title", content: `The Local Search Index | ${SITE.name}` },
        { property: "og:description", content: "Live weekly Map Pack volatility and local algorithm tracking." },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Dataset",
            name: "Map Observer Local Search Volatility Index",
            description:
              "Weekly index of Map Pack rank churn across a 100-query sample. Higher scores mean more local-search rerankings.",
            url,
            license: "https://creativecommons.org/licenses/by/4.0/",
            creator: { "@type": "Organization", name: SITE.name, url: SITE.url },
            isAccessibleForFree: true,
            keywords: ["local SEO", "Map Pack", "Google Business Profile", "algorithm updates"],
          }),
        },
      ],
    };
  },
  component: IndexPage,
});

function levelClass(level: string): string {
  switch (level) {
    case "turbulent": return "bg-red-600 text-white";
    case "stormy": return "bg-orange-500 text-white";
    case "active": return "bg-yellow-400 text-foreground";
    case "mild": return "bg-emerald-400 text-foreground";
    default: return "bg-green-500 text-white";
  }
}

/** Whole days between a snapshot date (YYYY-MM-DD) and now, UTC. */
function daysSince(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  const t = Date.parse(`${dateStr}T00:00:00.000Z`);
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 86_400_000);
}

/** The next Monday (UTC) at or after now, as a YYYY-MM-DD string. */
function nextMondayISO(): string {
  const d = new Date();
  const delta = (8 - d.getUTCDay()) % 7 || 7;
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}


function IndexPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        <Suspense fallback={<div className="py-24 text-center text-muted-foreground">Loading…</div>}>
          <IndexBody />
        </Suspense>
      </main>
      <SiteFooter />
    </div>
  );
}

function IndexBody() {
  const { data: snapshots } = useSuspenseQuery(snapshotsQuery);
  const { data: updates } = useSuspenseQuery(updatesQuery);
  const snaps = (snapshots as VolatilitySnapshot[]).slice().reverse(); // oldest → newest
  const latest = snaps[snaps.length - 1];
  const recentUpdates = (updates as AlgorithmUpdate[]).slice(0, 3);
  const staleDays = daysSince(latest?.snapshot_date);
  // More than ~10 days without a reading means the weekly capture has slipped.
  const isStale = staleDays !== null && staleDays > 10;


  // Build SVG sparkline
  const w = 600;
  const h = 140;
  const pad = 8;
  const max = 10;
  const stepX = snaps.length > 1 ? (w - pad * 2) / (snaps.length - 1) : 0;
  const pts = snaps.map((s, i) => {
    const x = pad + stepX * i;
    const y = pad + ((max - Number(s.score)) / max) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const path = pts.length > 0 ? `M ${pts.join(" L ")}` : "";

  return (
    <div className="mx-auto max-w-5xl px-5 sm:px-8 py-16">
      <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-wider text-muted-foreground">
        <ol className="flex items-center gap-2 flex-wrap">
          <li><Link to="/" className="hover:text-accent">Home</Link></li>
          <li aria-hidden>/</li>
          <li>Local Search Index</li>
        </ol>
      </nav>

      <header className="mt-6 max-w-2xl">
        <p className="eyebrow">Live data</p>
        <h1 className="headline-display mt-3 text-5xl">The Local Search Index</h1>
        <p className="mt-4 text-lg text-muted-foreground">
          A weekly volatility score for the Google Map Pack, sampled across 100 high-intent local
          queries. Free to cite.
        </p>
        {latest && (
          <p className="mt-3 text-sm">
            {isStale ? (
              <span className="inline-block border border-border bg-paper-soft px-3 py-2">
                <strong>Latest measurement: {formatDate(latest.snapshot_date)}</strong>{" "}
                <span className="text-muted-foreground">
                  ({staleDays} days ago). Sampling is currently interrupted; the next scheduled
                  capture is {formatDate(nextMondayISO())}. We publish the date of every reading
                  rather than backfilling estimates.
                </span>
              </span>
            ) : (
              <span className="text-muted-foreground">
                Captured every Monday. Latest measurement: <strong>{formatDate(latest.snapshot_date)}</strong>{" "}
                · next update {formatDate(nextMondayISO())}.
              </span>
            )}
          </p>
        )}
      </header>


      {latest && (
        <section className="mt-10 grid gap-6 md:grid-cols-[1fr_2fr]">
          <div className={`rounded-sm p-8 ${levelClass(latest.level)} flex flex-col justify-center`}>
            <p className="text-xs uppercase tracking-[0.2em] opacity-80">{isStale ? "Latest reading" : "This week"}</p>
            <p className="mt-2 text-6xl font-bold leading-none">{Number(latest.score).toFixed(1)}</p>
            <p className="mt-3 text-lg uppercase tracking-wider">{latest.level}</p>
            <p className="mt-2 text-xs opacity-80">
              {latest.queries_sampled} queries · {formatDate(latest.snapshot_date)}
            </p>
          </div>
          <div className="rounded-sm border border-border bg-paper-soft p-6">
            <p className="eyebrow">Trailing {snaps.length} weeks</p>
            <svg viewBox={`0 0 ${w} ${h}`} className="mt-2 w-full h-auto" role="img" aria-label="Volatility trend chart">
              <line x1={pad} y1={pad + (h - pad * 2) * 0.3} x2={w - pad} y2={pad + (h - pad * 2) * 0.3} stroke="currentColor" strokeOpacity="0.08" />
              <line x1={pad} y1={pad + (h - pad * 2) * 0.6} x2={w - pad} y2={pad + (h - pad * 2) * 0.6} stroke="currentColor" strokeOpacity="0.08" />
              {path && <path d={path} fill="none" stroke="currentColor" strokeWidth="2" className="text-accent" />}
              {snaps.map((s, i) => {
                const x = pad + stepX * i;
                const y = pad + ((max - Number(s.score)) / max) * (h - pad * 2);
                return <circle key={s.id} cx={x} cy={y} r="3" className="fill-accent" />;
              })}
            </svg>
            <div className="mt-2 flex justify-between text-[10px] uppercase tracking-wider text-muted-foreground">
              <span>{formatDate(snaps[0]?.snapshot_date)}</span>
              <span>{formatDate(snaps[snaps.length - 1]?.snapshot_date)}</span>
            </div>
          </div>
        </section>
      )}

      <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-b border-border py-4">
        <CiteThis
          title="Map Observer Local Search Volatility Index"
          url={`${SITE.url}/local-search-index`}
          publishedDate={latest?.snapshot_date}
        />
        <span className="text-xs text-muted-foreground">
          CC-BY 4.0 — link to this page when citing.
        </span>
      </div>

      <section className="mt-12">
        <div className="border-b border-foreground pb-3 mb-6 flex items-baseline justify-between">
          <h2 className="headline-display text-2xl">Recent updates</h2>
          <Link to="/updates" className="text-xs uppercase tracking-wider hover:text-accent">All updates →</Link>
        </div>
        {recentUpdates.length === 0 ? (
          <p className="text-sm text-muted-foreground">No updates tracked recently.</p>
        ) : (
          <ul className="space-y-4">
            {recentUpdates.map((u) => (
              <li key={u.id} className="flex items-baseline gap-4 border-b border-border pb-3">
                <span className="text-xs uppercase tracking-wider text-muted-foreground w-32 shrink-0">
                  {formatDate(u.started_at)}
                </span>
                <div className="grow">
                  <Link to="/updates/$slug" params={{ slug: u.slug }} className="font-semibold hover:text-accent">
                    {u.name}
                  </Link>
                  <p className="mt-1 text-sm text-muted-foreground">{u.summary}</p>
                </div>
                <span className="text-sm font-bold tabular-nums">{Number(u.severity).toFixed(1)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-12">
        <div className="border-b border-foreground pb-3 mb-6">
          <h2 className="headline-display text-2xl">Methodology</h2>
        </div>
        <div className="prose prose-sm max-w-none text-foreground/85 space-y-3">
          <p>
            Every Monday we capture the top three Map Pack results for a fixed sample of 100
            local-intent queries across service, retail, hospitality, professional, and home-service
            categories in major US metros.
          </p>
          <p>
            The volatility score is the percent of result slots that changed hands week-over-week,
            rescaled 0–10. A 5.0 means roughly half of the tracked slots reranked. Anything above
            7.0 historically correlates with a confirmed algorithm update inside two weeks.
          </p>
          <p>
            Sample, queries, and weekly raw data are released under CC-BY 4.0. Email us if you want
            the underlying dataset.
          </p>
        </div>
      </section>

      <NewsletterCTA />
    </div>
  );
}
